import { Component, Input, OnInit, ChangeDetectionStrategy, signal, inject, input, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TinyColor } from "@ctrl/tinycolor";
import { ThemeService } from "../../../../core-ui/services/theme.service";
import { CardComponent } from "../card/card.component";
import { HostBinding } from "@angular/core";
import { ButtonComponent } from "../button/button.component";
@Component({
  selector: "av-icon",
  templateUrl: "./icon.component.html",
  styleUrls: ["./icon.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class IconComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private themeService = inject(ThemeService);
  private card = inject(CardComponent, { optional: true });
  private button = inject(ButtonComponent, { optional: true });

  readonly name = input<string>(undefined);
  readonly type = input<"svg" | "png">("svg");
  readonly size = input<"xs" | "sm" | "md" | "lg">(undefined);
  @Input()
  set mode(value: "dark" | "light") {
    this._mode = value;
    this.url.set(this.getUrl());
  }
  get mode() {
    return this._mode;
  }

  private isDarkMode = signal(false);
  private isLightBackground = signal(false);
  private container = signal<"card" | "main" | "button">("main");
  private _mode;

  private buttonModeMap = {
    card: {
      flat: {
        basic: "mode",
        primary: "light",
        accent: "light",
        success: "light",
        warn: "light",
        danger: "light",
        link: "mode",
      },
      icon: {
        basic: "dark",
        primary: "light",
        accent: "light",
        success: "light",
        warn: "light",
        danger: "light",
        link: "mode",
      },
      basic: {
        basic: "mode",
        primary: "mode",
        accent: "mode",
        success: "mode",
        warn: "mode",
        danger: "mode",
        link: "mode",
      },
      stroked: {
        basic: "mode",
        primary: "mode",
        accent: "mode",
        success: "mode",
        warn: "mode",
        danger: "mode",
        link: "mode",
      },
    },
    main: {
      flat: {
        basic: "dark",
        primary: "light",
        accent: "light",
        success: "light",
        warn: "light",
        danger: "light",
        link: "background-color",
      },
      icon: {
        basic: "dark",
        primary: "light",
        accent: "light",
        success: "light",
        warn: "light",
        danger: "light",
        link: "background-color",
      },
      basic: {
        basic: "background-color",
        primary: "background-color",
        accent: "background-color",
        success: "background-color",
        warn: "background-color",
        danger: "background-color",
        link: "background-color",
      },
      stroked: {
        basic: "background-color",
        primary: "background-color",
        accent: "background-color",
        success: "background-color",
        warn: "background-color",
        danger: "background-color",
        link: "background-color",
      },
    },
  };

  url = signal<string>(undefined);

  constructor() {
    const card = this.card;
    const button = this.button;

    if (!!button) {
      this.container.set("button");
    } else if (!!card && card.type() !== "ghost") {
      this.container.set("card");
    } else {
      this.container.set("main");
    }
  }

  @HostBinding("class") get class() {
    return {
      [`size-${this.size()}`]: !!this.size(),
    };
  }

  ngOnInit(): void {
    this.themeService.themeChanged$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((theme) => {
      this.isDarkMode.set(theme?.darkMode);
      if (theme?.backgroundColor) {
        this.isLightBackground.set(
          new TinyColor(theme?.backgroundColor).isLight()
        );
      }
      this.url.set(this.getUrl());
    });
    if (!!this.button && !this.mode) {
      this.button.changes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((changes) => {
        if (changes.color?.currentValue !== changes.color?.previousValue) {
          this.url.set(this.getUrl());
        }
      });
    }
  }

  getUrl(): string {
    let mode = this.mode;
    if (!mode) {
      switch (this.container()) {
        case "main":
          mode = this.isLightBackground() ? "dark" : "light";
          break;
        case "card":
          if (this.card?.isSecondary) {
            mode = this.card?.isLightBackground ? "dark" : "light";
          } else {
            mode = this.isDarkMode() ? "light" : "dark";
          }

          break;
        case "button":
          const container = !!this.card ? "card" : "main";
          const result =
            this.buttonModeMap[container][this.button.ofType()][
              this.button.color()
            ];
          switch (result) {
            case "mode":
              mode = this.isDarkMode() ? "light" : "dark";
              break;
            case "background-color":
              mode = this.isLightBackground() ? "dark" : "light";
              break;
            default:
              mode = result as any;
          }
          break;
      }
    }
    return `modules/shared/assets/images/${this.name()}-${mode}.${this.type()}`;
  }

}
